import TTCodeBlockLowLight, {
  type CodeBlockLowlightOptions,
} from '@tiptap/extension-code-block-lowlight';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import hljs from 'highlight.js';
import sql from 'highlight.js/lib/languages/sql';
import python from 'highlight.js/lib/languages/python';
import { type LanguageFn, common, createLowlight } from 'lowlight';

interface ExtendedCodeBlockOptions extends CodeBlockLowlightOptions {
  enableToggleCodeBlock?: boolean;
  exitOnEmptyDocument?: boolean;
  tabContent: string;
}

type CodeBlockConfig = {
  lang: string;
  key: string;
  lib: LanguageFn;
  colors?: Record<string, string>;
};

/**
  To add support for other languages in code blocks, add the language to the
  CODE_BLOCK_LANGUAGES object below. You can also redefine colors for highlights.
  For example, to add support for TS:
  ```javascript
  import ts from 'highlight.js/lib/languages/typescript';
  export const CODE_BLOCK_LANGUAGES = {
    // ...
    ts: {
      lang: 'typescript',
      key: 'ts', // represents CODE_BLOCK_LANGUAGES[key]
      lib: ts,
      colors: {
        keyword: blue['blue-500'],
        attr: teal['teal-300'],
        comment: teal['teal-300'],
        operator: red['red-500'],
        // etc
      },
    },
  }
  ```
  And then you can pass the `CODE_BLOCK_LANGUAGES.ts.key` as `codeBlockLanguage` prop of the BasicRichTextEditor
  to enable sintax highlighting for typescript.
  By default `disableCodeBlockEscape` prop of the `BasicRichTextEditor` is `true`. That means that Command + Enter or Ctrl + Enter is disabled, the content passed to the editor will be wrapped in a code block with the language specified in the `codeBlockLanguage` prop. If you want to maintain mixed content (rich text and code bloks), pass `false` to the `disableCodeBlockEscape` prop of the BasicRichTextEditor. If `disableCodeBlockEscape` is `true`, we need content to be always wrapped in `<pre><code></code><pre>`, so if `initialValue` is empty or plain text pass it to `BasicRichTextEditor` as `initialValue={toCodeBlock(initialValue, CODE_BLOCK_LANGUAGES[language].lang)}`. 

  ```javascript
  <BasicRichTextEditor
    initialValue={toCodeBlock(
      value || '',
      CODE_BLOCK_LANGUAGES.ts.lang
    )}
    codeBlockLanguage={CODE_BLOCK_LANGUAGES.ts.key}
  />
  ```
 */
export const CODE_BLOCK_LANGUAGES: Record<string, CodeBlockConfig> = {
  sql: {
    lang: 'sql',
    key: 'sql',
    lib: sql,
  },
  python: {
    lang: 'python',
    key: 'python',
    lib: python,
    colors: {
      params: 'var(--color-code-highlight-params)',
    },
  },
};

const lowlight = createLowlight(common);

for (const { lang, lib } of Object.values(CODE_BLOCK_LANGUAGES)) {
  lowlight.register({ [lang]: lib });
}

/**
 * @remarks The highlight result is post-processed to preserve inline whitespce. This ensures
 * that multiple lines of comments are not merged into a single line. In that case, highlight
 * will produce html like
 * ```html
 * <span class="hljs-comment">comment 1</span>\n<span class="hljs-comment">comment 2</span>
 * ```
 * with a `\n` in-between two spans. If this is provided to TipTap, normal html whitespace
 * collapsing will take place even though the code block extension's `preserveWhitespace`
 * setting set to "full" in `parseHTML` - the newlines need to be moved into a span element
 * for the `preserveWhitespace` setting to take effect.
 */
export const toCodeBlock = (value = '', language = 'sql') => {
  const hljsResult = hljs.highlight(value, { language }).value;
  const withInlineWhitespace = hljsResult.replaceAll(
    /<\/span>(\s+)<span/g,
    '$1</span><span'
  );
  return `<pre><code class="hljs language-${language}">${withInlineWhitespace}</code></pre>`;
};

export const CodeBlock = TTCodeBlockLowLight.extend<ExtendedCodeBlockOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      exitOnEmptyDocument: true,
      enableToggleCodeBlock: true,
      tabContent: '\t',
      // code-block
      languageClassPrefix: 'language-',
      exitOnTripleEnter: true,
      exitOnArrowDown: true,
      defaultLanguage: null,
      HTMLAttributes: {},
      // code-block-lowlight
      lowlight,
    };
  },
  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        filterTransaction(tr, state) {
          // When all content is selected via mouse and deleted/backspaced, ProseMirror appends
          // a transaction that converts the document to an empty paragraph. This happens because
          // during applyTransaction, ProseMirror sees an empty code block and considers it invalid,
          // appending a fixup transaction before our setNewCodeBlock transaction has a chance to
          // fully settle. We block this fixup by rejecting any transaction that would convert a
          // code block to a paragraph — i.e. where the current state has a code block but the
          // incoming transaction would replace it with a paragraph.
          if (
            tr.doc.firstChild?.type.name === 'paragraph' &&
            state.doc.firstChild?.type.name === 'codeBlock'
          ) {
            return false;
          }
          return true;
        },
      }),
    ];
  },
  addKeyboardShortcuts() {
    const setNewCodeBlock = () => {
      const { doc, schema, tr } = this.editor.state;

      const codeBlock = schema.nodes.codeBlock.create({
        language: this.options.defaultLanguage,
      });

      const transaction = tr.replaceWith(0, doc.content.size, codeBlock);
      transaction.setSelection(TextSelection.near(transaction.doc.resolve(1)));

      this.editor.view.dispatch(transaction);
    };

    const handleDelete = () => {
      const { doc } = this.editor.state;
      const { empty, $anchor, from, to } = this.editor.state.selection;
      const isAtStart = $anchor.pos === 1;
      const isAllSelected = from <= 1 && to >= doc.content.size - 1;
      const isDocumentEmpty = isAtStart && !$anchor.parent.textContent.length;

      if (!this.options.exitOnEmptyDocument && isAllSelected) {
        setNewCodeBlock();
        return true;
      }

      if (!empty || $anchor.parent.type.name !== this.name) {
        return false;
      }

      if (!this.options.exitOnEmptyDocument && isDocumentEmpty) {
        setNewCodeBlock();
        return true;
      }

      return false;
    };

    return {
      ...this.parent?.(),
      Delete: handleDelete,
      Backspace: handleDelete,
      Enter: ({ editor }) => {
        const { doc } = this.editor.state;
        const { $anchor, from, to } = this.editor.state.selection;
        const isAtStart = $anchor.pos === 1;
        const isAllSelected = from === 1 && to === doc.content.size - 1;
        const isDocumentEmpty = isAtStart && !$anchor.parent.textContent.length;

        if (
          !this.options.exitOnEmptyDocument &&
          !isDocumentEmpty &&
          isAllSelected
        ) {
          setNewCodeBlock();
          return true;
        }

        return this.parent?.()?.Enter?.({ editor }) ?? false;
      },
      'Mod-Alt-c': () => {
        return !this.options.enableToggleCodeBlock;
      },
      'Mod-Enter': () => {
        return !this.options.enableToggleCodeBlock;
      },
      'Shift-Tab': () => {
        const { state } = this.editor;
        const { doc, selection } = state;

        if (selection.empty) {
          const before = doc.resolve(selection.from).nodeBefore;

          if (
            before &&
            before.type.name === 'text' &&
            before.text?.endsWith(this.options.tabContent)
          ) {
            return this.editor
              .chain()
              .command(({ state, tr }) => {
                const { from } = state.selection;

                tr.delete(from - this.options.tabContent.length, from);

                return true;
              })
              .run();
          }
        }

        return false;
      },
      Tab: () => {
        if (this.options.tabContent) {
          return this.editor.commands.insertContent(this.options.tabContent);
        }

        return false;
      },
    };
  },
});
